
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Response} from '../core/Response';
import {FieldModelResponse} from '../core/Response';
import {LegalDocumentDescription} from './LegalDocumentDescription';
import {FieldModelLegalDocumentDescription} from './LegalDocumentDescription';

/**
 * LegalDocumentGetResponse struct
 */
class LegalDocumentGetResponse extends Response {
  /**
   * Initialize struct
   * @param {!Response=} parent
   * @param {!LegalDocumentDescription=} description
   * @param {!Uint8Array=} documentContent
   * @constructor
   */
  constructor (parent = new Response(), description = new LegalDocumentDescription(), documentContent = new Uint8Array(0)) {
    super()
    super.copy(parent)
    this.Description = description
    this.DocumentContent = documentContent
  }

  /**
   * Copy struct (shallow copy)
   * @this {!LegalDocumentGetResponse}
   * @param {!LegalDocumentGetResponse} other Other struct
   * @returns {!LegalDocumentGetResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Description != null) {
      this.Description = LegalDocumentDescription.fromObject(other.Description)
    } else {
      this.Description = null
    }
    if (other.DocumentContent != null) {
      if (typeof other.DocumentContent === 'string') {
        // noinspection JSUnresolvedFunction
        this.DocumentContent = Uint8Array.from(Buffer.from(other.DocumentContent, 'base64'))
      } else {
        this.DocumentContent = Uint8Array.from(other.DocumentContent)
      }
    } else {
      this.DocumentContent = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!LegalDocumentGetResponse}
   * @returns {!LegalDocumentGetResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new LegalDocumentGetResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new LegalDocumentGetResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!LegalDocumentGetResponse}
   * @param {!LegalDocumentGetResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof LegalDocumentGetResponse)) {
      throw new TypeError('Instance of LegalDocumentGetResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!LegalDocumentGetResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Description: ((this.Description != null) ? this.Description : null),
      DocumentContent: ((this.DocumentContent != null) ? Buffer.from(this.DocumentContent).toString('base64') : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return LegalDocumentGetResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!LegalDocumentGetResponse} other Object value
   * @returns {!LegalDocumentGetResponse} Created struct
   */
  static fromObject (other) {
    return new LegalDocumentGetResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!LegalDocumentGetResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return LegalDocumentGetResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!LegalDocumentGetResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5556
  }
}

export { LegalDocumentGetResponse };

/**
 * Fast Binary Encoding LegalDocumentGetResponse field model
 */
class FieldModelLegalDocumentGetResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelResponse(buffer, 4 + 4)
    this.Description = new FieldModelLegalDocumentDescription(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.DocumentContent = new fbe.FieldModelBytes(buffer, this.Description.fbeOffset + this.Description.fbeSize)
  }

  /**
   * Get the core.Response field model
   * @this {!FieldModelLegalDocumentGetResponse}
   * @returns {!FieldModelResponse} core.Response field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelLegalDocumentGetResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelLegalDocumentGetResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Description.fbeSize + this.DocumentContent.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelLegalDocumentGetResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Description.fbeExtra + this.DocumentContent.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelLegalDocumentGetResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelLegalDocumentGetResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelLegalDocumentGetResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5556
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelLegalDocumentGetResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelLegalDocumentGetResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Description.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Description.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Description.fbeSize

    if ((fbeCurrentSize + this.DocumentContent.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DocumentContent.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DocumentContent.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelLegalDocumentGetResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelLegalDocumentGetResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelLegalDocumentGetResponse}
   * @param {!LegalDocumentGetResponse} fbeValue Default value, defaults is new LegalDocumentGetResponse()
   * @returns {!LegalDocumentGetResponse} LegalDocumentGetResponse value
   */
  get (fbeValue = new LegalDocumentGetResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelLegalDocumentGetResponse}
   * @param {!LegalDocumentGetResponse} fbeValue LegalDocumentGetResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Description.fbeSize) <= fbeStructSize) {
      fbeValue.Description = this.Description.get()
    } else {
      fbeValue.Description = new LegalDocumentDescription()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Description.fbeSize

    if ((fbeCurrentSize + this.DocumentContent.fbeSize) <= fbeStructSize) {
      fbeValue.DocumentContent = this.DocumentContent.get()
    } else {
      fbeValue.DocumentContent = new Uint8Array(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DocumentContent.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelLegalDocumentGetResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelLegalDocumentGetResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelLegalDocumentGetResponse}
   * @param {!LegalDocumentGetResponse} fbeValue LegalDocumentGetResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelLegalDocumentGetResponse}
   * @param {LegalDocumentGetResponse} fbeValue LegalDocumentGetResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Description.set(fbeValue.Description)
    this.DocumentContent.set(fbeValue.DocumentContent)
  }
}

export { FieldModelLegalDocumentGetResponse };

/**
 * Fast Binary Encoding LegalDocumentGetResponse model
 */
class LegalDocumentGetResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelLegalDocumentGetResponse(this.buffer, 4)
  }

  /**
   * Get the LegalDocumentGetResponse model
   * @this {!LegalDocumentGetResponseModel}
   * @returns {!FieldModelLegalDocumentGetResponse} model LegalDocumentGetResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!LegalDocumentGetResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!LegalDocumentGetResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return LegalDocumentGetResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!LegalDocumentGetResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelLegalDocumentGetResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!LegalDocumentGetResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!LegalDocumentGetResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!LegalDocumentGetResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!LegalDocumentGetResponseModel}
   * @param {!LegalDocumentGetResponse} value LegalDocumentGetResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!LegalDocumentGetResponseModel}
   * @param {!LegalDocumentGetResponse} value LegalDocumentGetResponse value, defaults is new LegalDocumentGetResponse()
   * @return {!object} Deserialized LegalDocumentGetResponse value and its size
   */
  deserialize (value = new LegalDocumentGetResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new LegalDocumentGetResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new LegalDocumentGetResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!LegalDocumentGetResponseModel}
   * @param {!number} prev Previous LegalDocumentGetResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { LegalDocumentGetResponseModel };
